/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useModalV2Context } from 'src/features/modal-v2'
import { Nullable } from 'src/types/main'

import { DonationIdentityCollectorContext } from './donation-identity-collector.context'
import {
  DonorIdentity,
  getLocalStorageDonorIdentity,
  setLocalStorageDonorIdentity
} from './donation-identity-collector.services'
import { IdentityCollectorModal } from './ui/IdentityCollectorModal'

const DONOR_IDENTITY_PLACEHOLDER: DonorIdentity = {
  email: '',
  ic_number: '',
  name: '',
  phone_number: '',
  type: 'national_id'
}

/**
 * [useDonorIdentity description]
 * @type {[type]}
 */
export const useDonorIdentity = () => {
  const [donorIdentity, _setDonorIdentity] = useState<Nullable<Partial<DonorIdentity>>>(null)
  const [donorIdentityId, setDonorIdentityId] = useState<Nullable<string>>(null)

  // const { setDonorIdentity: setDonorIdentityInContext } = useDonationIdentityCollectorContext()

  useEffect(() => {
    const data = getLocalStorageDonorIdentity()

    if (data) {
      _setDonorIdentity(data.user_info || DONOR_IDENTITY_PLACEHOLDER)
      setDonorIdentityId(data.user_profile_id || null)
    } else {
      _setDonorIdentity(DONOR_IDENTITY_PLACEHOLDER)
    }
  }, [])

  const setDonorIdentity = useCallback((data: Partial<DonorIdentity>) => {
    _setDonorIdentity((prev) => ({
      ...prev,
      ...data
    }))
  }, [])

  const identityIsValid = useMemo(() => {
    if (!donorIdentity || !donorIdentityId) {
      return false
    } else {
      const { email, ic_number, name, phone_number, type } = donorIdentity
      if (!email || !ic_number || !name || !phone_number || !type) {
        return false
      }
    }

    return true
  }, [donorIdentity, donorIdentityId])

  const storePersistentDonorIdentityData = useCallback((donorIdentityId: string) => {
    // if (!identityIsValid) throw new Error()

    setLocalStorageDonorIdentity({
      user_info: {...donorIdentity as DonorIdentity},
      user_profile_id: donorIdentityId
    })
  }, [donorIdentity])

  /*const saveDonorIdentityData = useCallback(() => {
    setDonorIdentityInContext(donorIdentity)
    setLocalStorageDonorIdentity(donorIdentity)
  }, [donorIdentity, setDonorIdentityInContext])*/

  return {
    donorIdentity,
    donorIdentityId,
    identityIsValid,
    setDonorIdentity,
    setDonorIdentityId,
    storePersistentDonorIdentityData
  }
}

const COLLECTOR_MODAL_KEY = 'donor-identity-collector'
const IDENTITY_CONFIRM_MODAL_KEY = 'donor-identity-confirm'

/**
 * [description]
 * @return {[type]} [description]
 */
export const useDonorIdentityCollectorModal = () => {
  const { openModal, closeModal } = useModalV2Context()

  const openCollectorModal = useCallback(() => {
    openModal(
      {
        content: <IdentityCollectorModal />,
        dialogTitle: 'Fill the Donor Identity form'
      },
      COLLECTOR_MODAL_KEY
    )
  }, [openModal])

  const closeCollectorModal = useCallback(() => {
    closeModal(COLLECTOR_MODAL_KEY)
  }, [closeModal])

  const openIdentityConfirmModal = useCallback(() => {
    openModal(
      {
        content: <IdentityCollectorModal identityConfirm />,
        dialogTitle: 'Confirm your identity'
      },
      IDENTITY_CONFIRM_MODAL_KEY
    )
  }, [openModal])

  const closeIdentityConfirmModal = useCallback(() => {
    closeModal(IDENTITY_CONFIRM_MODAL_KEY)
  }, [closeModal])

  return {
    openCollectorModal,
    closeCollectorModal,
    openIdentityConfirmModal,
    closeIdentityConfirmModal
  }
}

/**
 * [description]
 * @return {[type]} [description]
 */
export const useDonationIdentityCollectorContext = () => {
  return useContext(DonationIdentityCollectorContext)
}
